import { useState } from 'react'
import { useAppSelector } from '@/redux/hooks'
import { ReverLoading, SuccessIcon } from '@itsrever/design-system'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import MagicModal from '@/ui/components/magic-modal/magic-modal'

import { useSendProductRequest } from '@/application/send-product-request'
import featuredProducts from './featured-products'
export const FeaturedReverProducts = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )

  let displayProducts = featuredProducts.filter((product) => {
    return !(product.onlyShopify && settings.platform !== 'SHOPIFY')
  })
  displayProducts = displayProducts.slice(0, 3)

  const email =
    useAppSelector((store) => store.userApi.getMe.response.user?.name) ?? ''
  const [selectedProductIndex, setSelectedProductIndex] = useState<
    number | undefined
  >(undefined)

  const featureText = t(
    'featured_products.' + displayProducts[selectedProductIndex ?? 0].name
  )
  const { sendProductRequest, isLoading, error, success } =
    useSendProductRequest()

  const handleClickRequest = () => {
    sendProductRequest({
      email,
      featureText: featureText,
      source: 'product-request'
    })
  }

  const [isModalOpen, setIsModalOpen] = useState(false)

  const onClickProduct = (index: number) => {
    setSelectedProductIndex(index)
    setIsModalOpen(true)
  }

  return (
    <>
      <div className="mb-5">
        <p className="font-logo mb-2 text-2xl font-semibold uppercase text-slate-700">
          {t('featured_products.title')}
        </p>
        <div className="grid w-full grid-cols-3 gap-4">
          {displayProducts.map((product, index) => (
            <div key={index} className="relative">
              <MagicModal
                modalId={'productModal-' + index}
                onCloseModal={() => setIsModalOpen(false)}
                showModal={isModalOpen && selectedProductIndex === index}
                targetId={'productId-' + index}
              >
                <div
                  className={
                    'flex h-full items-center gap-4 ' +
                    (product.layout === 'horizontal' ? 'flex-col' : '')
                  }
                >
                  <img
                    className={
                      'scale-[1.05] rounded-xl drop-shadow-lg ' +
                      (product.layout === 'horizontal'
                        ? ' w-5/6 h-72 -translate-y-6 object-cover'
                        : 'h-full -translate-x-5')
                    }
                    src={product.mainImage}
                  />

                  <div
                    className={
                      ' flex h-full flex-col items-start justify-start text-left ' +
                      (product.layout === 'horizontal'
                        ? 'pb-10 mx-11'
                        : 'py-10 -translate-x-5 pl-3 mx-3')
                    }
                  >
                    <div className="flex w-full items-center justify-between">
                      {product.isNew && (
                        <p
                          style={{
                            color: 'rgb(23, 144, 255)'
                          }}
                          className="font-logo uppercase"
                        >
                          {t('featured_products.new')}
                        </p>
                      )}
                    </div>
                    <div className="mb-2 flex items-center gap-4 text-3xl font-semibold text-slate-800">
                      {t('featured_products.' + product.name)}
                      {product.name === 'tracking_interface.name' && (
                        <p
                          className="mx-auto -skew-x-6 cursor-pointer rounded-md px-3 py-1 text-white transition-shadow hover:shadow-lg"
                          style={{
                            backgroundColor:
                              product.mode === 'info'
                                ? '#84cc16'
                                : 'rgb(23, 144, 255)'
                          }}
                        >
                          {t('featured_products.' + product.ctaText)}
                        </p>
                      )}
                    </div>
                    {product.name === 'tracking_interface.name' && (
                      <div className="mt-4 w-64 rounded">
                        <iframe
                          src="https://www.linkedin.com/embed/feed/update/urn:li:ugcPost:7161285452694437888?compact=1"
                          height="240"
                          width="480"
                          title="Tracking Video"
                        ></iframe>
                      </div>
                    )}
                    <p
                      className="mt-5 text-left text-base font-light text-slate-600"
                      dangerouslySetInnerHTML={{
                        __html: t(
                          'featured_products.' + product.longDescription
                        )
                      }}
                    />

                    <ul className="mt-5 flex flex-col gap-2 px-3 text-left text-base font-light text-slate-600">
                      {product.features?.map((feature) => (
                        <li key={feature} className="list-disc">
                          <p
                            dangerouslySetInnerHTML={{
                              __html: t('featured_products.' + feature)
                            }}
                          />
                        </li>
                      ))}
                    </ul>

                    {success ? (
                      <div className="mt-8 flex items-center justify-center gap-4 ">
                        <SuccessIcon className="size-10" />
                        <p className="text-sm font-light text-slate-600">
                          {t('featured_products.requested')}
                        </p>
                      </div>
                    ) : isLoading ? (
                      <ReverLoading className="mx-auto mt-2" />
                    ) : error ? (
                      <div className="mt-8 flex items-center justify-center gap-4 text-red-500">
                        <p className="text-sm font-light">
                          Something went wrong. Please try again.
                        </p>
                      </div>
                    ) : (
                      product.name !== 'tracking_interface.name' && (
                        <button
                          className="mx-auto mt-8 -skew-x-6 cursor-pointer rounded-md px-3 py-1 text-white transition-shadow hover:shadow-lg"
                          style={{
                            backgroundColor:
                              product.mode === 'info'
                                ? '#84cc16'
                                : 'rgb(23, 144, 255)'
                          }}
                          onClick={() => {
                            if (product.redirectUrl) {
                              navigate(product.redirectUrl)
                            }
                            handleClickRequest()
                          }}
                        >
                          {product.ctaText
                            ? t('featured_products.' + product.ctaText)
                            : product.mode === 'info'
                              ? t('featured_products.discover_feature')
                              : t('featured_products.request_access')}
                        </button>
                      )
                    )}
                  </div>
                </div>
              </MagicModal>

              <div
                onClick={() => onClickProduct(index)}
                key={index}
                id={'productId-' + index}
                className={
                  'group relative h-32 w-full cursor-pointer flex-col overflow-hidden rounded-lg bg-white shadow transition-all duration-500 ease-in hover:shadow-lg '
                }
              >
                <div className="flex h-full items-center gap-4">
                  <img
                    className={
                      'h-full max-w-[5rem] object-cover scale-[1] transition-all duration-1000 group-hover:-translate-x-0 group-hover:translate-y-0 group-hover:scale-[1.1] ' +
                      (product.layout === 'horizontal'
                        ? ' object-cover w-20 -translate-x-0'
                        : '-translate-x-0 translate-y-0')
                    }
                    src={product.previewImage}
                    style={{
                      boxShadow: '10px 0px 7px -5px rgb(0 0 0 / 0.1)'
                    }}
                  />

                  <div className="flex size-full flex-col items-start justify-center py-2 pr-1 text-left">
                    <div className="flex w-full items-center justify-between">
                      {product.isNew && (
                        <p
                          style={{
                            color: 'rgb(23, 144, 255)'
                          }}
                          className="font-logo"
                        >
                          {t('featured_products.new')}
                        </p>
                      )}

                      <p
                        className="absolute right-2 top-2 mb-2 ml-auto flex -skew-x-6 items-center justify-center rounded-md px-2 py-1 text-xs text-white shadow-sm transition-all duration-500 hover:shadow-lg"
                        style={{
                          backgroundColor:
                            product.mode === 'info'
                              ? '#84cc16'
                              : 'rgb(23, 144, 255)'
                        }}
                      >
                        {product.mode === 'info'
                          ? t('featured_products.discover')
                          : t('featured_products.request')}
                      </p>
                    </div>
                    <p className="mb-2 text-lg font-semibold leading-5 text-slate-800">
                      {t('featured_products.' + product.name)}
                    </p>
                    <p className="text-left text-sm font-light leading-4 text-slate-600">
                      {t('featured_products.' + product.description)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
